import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, Pencil, Trash2, Search, Phone, Mail, MapPin, Loader2, ChevronDown, ChevronUp, Globe, User, Smartphone, Navigation, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../services/api';
import SyncCompaniesModal from '../components/SyncCompaniesModal';

export default function CompaniesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [aiMode, setAiMode] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_superadmin;

  const load = useCallback(async () => {
    try { const r = await api.get('/system/companies', { params: { search: search || undefined } }); setCompanies(r.data.companies); }
    catch {} finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing?.id) { await api.put(`/system/companies/${editing.id}`, data); toast.success(t('common.saved')); }
      else { await api.post('/system/companies', data); toast.success(t('companies.companyAdded')); }
      setShowForm(false); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/system/companies/${deleteTarget.id}`); toast.success(t('common.deleted')); setDeleteTarget(null); load(); }
    catch { toast.error(t('common.error')); }
  };

  const handleAiParse = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.post('/ai/parse-company', { text: aiText });
      if (r.data.company) {
        setEditing(null);
        setShowForm(true);
        // Pre-fill — set editing to parsed data (without id = new)
        setTimeout(() => {
          const formEl = document.querySelector('[data-company-form]');
          if (formEl) formEl.dispatchEvent(new CustomEvent('prefill', { detail: r.data.company }));
        }, 100);
        setAiMode(false); setAiText('');
      }
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Building2 size={28} className="text-indigo-600" /> {t('companies.title')}
          </h1>
          <p className="text-gray-500 mt-1">{t('companies.companyCount', { count: companies.length })}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => setShowSyncModal(true)}
              className="flex items-center gap-1 px-3 py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg text-sm">
              <RefreshCw size={14} /> Synchronizuj z NPR
            </button>
          )}
          <button onClick={() => { setAiMode(true); }}
            className="flex items-center gap-1 px-3 py-2 border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-lg text-sm">
            {t('companies.addWithAi')}
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm">
            <Plus size={16} /> {t('companies.newCompany')}
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('companies.searchPlaceholder')}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm" />
      </div>

      {loading ? <div className="p-8 text-gray-400">{t('common.loading')}</div> : companies.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Building2 size={48} className="mx-auto mb-4 opacity-50" />
          <p>{search ? t('common.noResults') : t('companies.noCompanies')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {companies.map(c => (
            <CompanyCard key={c.id} company={c} onEdit={() => { setEditing(c); setShowForm(true); }} onDelete={() => setDeleteTarget(c)} />
          ))}
        </div>
      )}

      {/* Form modal */}
      <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? t('companies.editCompany') : t('companies.newCompany')} maxWidth="max-w-2xl">
        <CompanyForm initial={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
      </Modal>

      {/* AI parse modal */}
      <Modal isOpen={aiMode} onClose={() => setAiMode(false)} title={t('companies.aiParseTitle')}>
        <div className="space-y-3">
          <p className="text-sm text-gray-500">{t('companies.aiParseDescription')}</p>
          <textarea value={aiText} onChange={e => setAiText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={6}
            placeholder={t('companies.aiParsePlaceholder')} autoFocus />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAiMode(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
            <button onClick={async () => {
              setAiLoading(true);
              try {
                const r = await api.post('/ai/parse-company', { text: aiText });
                if (r.data.company) { setEditing(r.data.company); setAiMode(false); setShowForm(true); setAiText(''); }
              } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
              finally { setAiLoading(false); }
            }} disabled={!aiText.trim() || aiLoading}
              className="flex items-center gap-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm">
              {aiLoading ? <><Loader2 size={14} className="animate-spin" /> {t('companies.aiAnalyzing')}</> : t('companies.aiRecognize')}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteTarget} type="danger"
        title={t('companies.deleteCompanyTitle')} message={t('companies.deleteConfirm', { name: deleteTarget?.name })}
        onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />

      <SyncCompaniesModal isOpen={showSyncModal} onClose={() => setShowSyncModal(false)} onDone={load} />
    </div>
  );
}

function CompanyCard({ company: c, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const address = [c.address_street, c.address_zip, c.address_city].filter(Boolean).join(', ');
  const hasDetails = c.nip || c.regon || c.contact_person || c.email || c.phone || c.phone_mobile || c.website || address || c.contact_phone || c.contact_email || c.notes;

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition">
      <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => hasDetails && setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 text-lg">{c.name}</span>
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${c.company_type === 'partner' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
              {c.company_type === 'partner' ? 'Współpraca' : 'Klient'}
            </span>
          </div>
          {c.short_name && c.short_name !== c.name && <div className="text-sm text-gray-500">{c.short_name}</div>}
          {!expanded && (
            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1 flex-wrap">
              {c.nip && <span>NIP: {c.nip}</span>}
              {c.address_city && <span className="flex items-center gap-1"><MapPin size={12} />{c.address_city}</span>}
              {address && <a href={`https://www.google.com/maps/search/${encodeURIComponent(address)}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="bg-green-100 hover:bg-green-200 text-green-700 p-1.5 sm:p-1 rounded transition" title="Nawiguj"><Navigation size={13} className="hidden sm:block" /><Navigation size={20} className="block sm:hidden" /></a>}
              {c.contact_person && <>
                <span className="text-gray-400">|</span>
                <span className="flex items-center gap-1"><User size={12} />Kontakt: <strong>{c.contact_person}</strong>{c.contact_position ? ` (${c.contact_position})` : ''}</span>
              </>}
              {c.contact_phone && <a href={`tel:${c.contact_phone}`} onClick={e => e.stopPropagation()} className="text-blue-600 hover:underline flex items-center gap-1"><Phone size={12} />{c.contact_phone}</a>}
              {c.contact_email && <a href={`mailto:${c.contact_email}`} onClick={e => e.stopPropagation()} className="text-blue-600 hover:underline">{c.contact_email}</a>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1.5 hover:bg-gray-100 rounded"><Pencil size={14} className="text-gray-500" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
          {hasDetails && (expanded ? <ChevronUp size={16} className="text-gray-400 ml-1" /> : <ChevronDown size={16} className="text-gray-400 ml-1" />)}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          {/* Lewa kolumna — dane firmy, adres, uwagi */}
          <div className="space-y-1.5">
            {c.nip && <div className="text-gray-600"><span className="text-gray-400 text-xs uppercase mr-2">NIP</span>{c.nip}</div>}
            {c.regon && <div className="text-gray-600"><span className="text-gray-400 text-xs uppercase mr-2">REGON</span>{c.regon}</div>}
            {c.krs && <div className="text-gray-600"><span className="text-gray-400 text-xs uppercase mr-2">KRS</span>{c.krs}</div>}
            {c.email && <div className="flex items-center gap-2 text-gray-600"><Mail size={13} className="text-gray-400" /><a href={`mailto:${c.email}`} className="text-blue-600 hover:underline">{c.email}</a></div>}
            {c.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-gray-400" /><a href={`tel:${c.phone}`} className="text-blue-600 hover:underline">{c.phone}</a></div>}
            {c.phone_mobile && <div className="flex items-center gap-2"><Smartphone size={13} className="text-gray-400" /><a href={`tel:${c.phone_mobile}`} className="text-blue-600 hover:underline">{c.phone_mobile}</a></div>}
            {c.website && <div className="flex items-center gap-2 text-gray-600"><Globe size={13} className="text-gray-400" /><a href={c.website.startsWith('http') ? c.website : 'https://' + c.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{c.website}</a></div>}
            {address && (
              <div className="flex items-start gap-2 text-gray-600 mt-2"><MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />{address}{c.address_country && c.address_country !== 'Polska' ? ', ' + c.address_country : ''}</div>
            )}
            {c.address_montage && (
              <div className="flex items-start gap-2 text-gray-600"><MapPin size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />{c.address_montage} <span className="text-gray-400 text-xs">(montaż)</span></div>
            )}
            {c.notes && (
              <div className="mt-2">
                <div className="text-gray-400 text-xs uppercase mb-0.5">Uwagi</div>
                <div className="text-gray-600">{c.notes}</div>
              </div>
            )}
          </div>
          {/* Prawa kolumna — osoba kontaktowa */}
          <div className="space-y-1.5">
            {c.contact_person && (
              <div>
                <div className="text-gray-400 text-xs uppercase mb-0.5">Osoba kontaktowa</div>
                <div>
                  <span className="text-gray-700 font-medium"><User size={13} className="inline mr-1 text-gray-400" />{c.contact_person}</span>
                  {c.contact_position && <span className="text-gray-500 text-sm"> — {c.contact_position}</span>}
                </div>
                {c.contact_phone && <div className="flex items-center gap-2 text-sm mt-0.5"><Phone size={12} className="text-gray-400" /><a href={`tel:${c.contact_phone}`} className="text-blue-600 hover:underline">{c.contact_phone}</a></div>}
                {c.contact_email && <div className="flex items-center gap-2 text-sm mt-0.5"><Mail size={12} className="text-gray-400" /><a href={`mailto:${c.contact_email}`} className="text-blue-600 hover:underline">{c.contact_email}</a></div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyForm({ initial, onSave, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: initial?.name || '', short_name: initial?.short_name || '', company_type: initial?.company_type || 'client',
    nip: initial?.nip || '', regon: initial?.regon || '', krs: initial?.krs || '',
    contact_person: initial?.contact_person || '', contact_phone: initial?.contact_phone || '', contact_email: initial?.contact_email || '', contact_position: initial?.contact_position || '',
    email: initial?.email || '', phone: initial?.phone || '', phone_mobile: initial?.phone_mobile || '', website: initial?.website || '',
    address_street: initial?.address_street || '', address_city: initial?.address_city || '',
    address_zip: initial?.address_zip || '', address_country: initial?.address_country || t('common.poland'),
    address_montage: initial?.address_montage || '', notes: initial?.notes || '',
  });
  const ic = "w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm";
  const lc = "block text-xs font-medium text-gray-600 mb-1";

  const [gusLoading, setGusLoading] = useState(false);
  const toast = useToast();

  const lookupGus = async () => {
    if (!form.nip || form.nip.replace(/\D/g, '').length !== 10) { toast.error(t('companies.nipInvalid')); return; }
    setGusLoading(true);
    try {
      const r = await api.post('/ai/gus-lookup', { nip: form.nip });
      if (r.data.company) {
        setForm(f => ({...f, ...Object.fromEntries(Object.entries(r.data.company).filter(([k,v]) => v && k !== 'found' && k !== 'nip'))}));
        toast.success(t('companies.gusDataFetched'));
      } else { toast.error(t('companies.gusNotFound')); }
    } catch { toast.error(t('companies.gusSearchError')); }
    finally { setGusLoading(false); }
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2"><label className={lc}>{t('companies.companyNameLabel')}</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={ic} required autoFocus /></div>
        <div><label className={lc}>Rodzaj</label>
          <select value={form.company_type} onChange={e => setForm({...form, company_type: e.target.value})} className={ic}>
            <option value="client">Klient</option>
            <option value="partner">Współpraca</option>
          </select>
        </div>
      </div>
      <div>
        <label className={lc}>{t('companies.shortNameLabel')}</label><input type="text" value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} className={ic} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={lc}>{t('common.nip')}</label>
          <div className="flex gap-1">
            <input type="text" value={form.nip} onChange={e => setForm({...form, nip: e.target.value})} className={ic} />
            <button type="button" onClick={lookupGus} disabled={gusLoading}
              className="px-2 py-1 bg-blue-600 disabled:opacity-40 text-white rounded text-xs flex-shrink-0 whitespace-nowrap">
              {gusLoading ? '...' : 'GUS'}
            </button>
          </div>
        </div>
        <div><label className={lc}>{t('common.regon')}</label><input type="text" value={form.regon} onChange={e => setForm({...form, regon: e.target.value})} className={ic} /></div>
        <div><label className={lc}>{t('common.krs')}</label><input type="text" value={form.krs} onChange={e => setForm({...form, krs: e.target.value})} className={ic} /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className={lc}>{t('common.email')}</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={ic} /></div>
        <div><label className={lc}>{t('common.phone')}</label><input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={ic} /></div>
        <div><label className={lc}>Tel. komórkowy</label><input type="text" value={form.phone_mobile} onChange={e => setForm({...form, phone_mobile: e.target.value})} className={ic} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lc}>{t('common.website')}</label><input type="text" value={form.website} onChange={e => setForm({...form, website: e.target.value})} className={ic} /></div>
      </div>
      <div className="border-t pt-3 mt-1">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Osoba kontaktowa</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lc}>Imię i nazwisko</label><input type="text" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} className={ic} /></div>
          <div><label className={lc}>Stanowisko</label><input type="text" value={form.contact_position} onChange={e => setForm({...form, contact_position: e.target.value})} className={ic} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lc}>Telefon</label><input type="text" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className={ic} /></div>
          <div><label className={lc}>E-mail</label><input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className={ic} /></div>
        </div>
      </div>
      <div className="border-t pt-3 mt-1">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('companies.addressHeading')}</h3>
        <div><label className={lc}>{t('common.streetAndNumber')}</label><input type="text" value={form.address_street} onChange={e => setForm({...form, address_street: e.target.value})} className={ic} /></div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div><label className={lc}>{t('common.zipCode')}</label><input type="text" value={form.address_zip} onChange={e => setForm({...form, address_zip: e.target.value})} className={ic} /></div>
          <div><label className={lc}>{t('common.city')}</label><input type="text" value={form.address_city} onChange={e => setForm({...form, address_city: e.target.value})} className={ic} /></div>
          <div><label className={lc}>{t('common.country')}</label><input type="text" value={form.address_country} onChange={e => setForm({...form, address_country: e.target.value})} className={ic} /></div>
        </div>
      </div>
      <div>
        <label className={lc}>{t('companies.montageAddress')}</label>
        <input type="text" value={form.address_montage} onChange={e => setForm({...form, address_montage: e.target.value})} className={ic} placeholder={t('companies.montageAddressPlaceholder')} />
      </div>
      <div>
        <label className={lc}>{t('common.notes')}</label>
        <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className={ic} rows={2} />
      </div>
      <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-1">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
        <button type="submit" className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">{initial?.id ? t('common.save') : t('companies.addCompanyButton')}</button>
      </div>
    </form>
  );
}
